
import { isEqual } from 'lodash';
import { defaults } from 'react-chartjs-2';

import { defaultFontFamily } from 'common/utils/fonts';

export default function customizeChartJSTooltip() {
  defaults.global.defaultFontFamily = defaultFontFamily();
  defaults.global.defaultFontSize = 14;
  defaults.global.defaultFontColor = '#77777C';
  defaults.global.elements.line.borderCapStyle = 'round';
  defaults.global.elements.point.hitRadius = 10;
  defaults.global.elements.point.hoverRadius = 10;
  defaults.global.legend.position = 'bottom';
  defaults.global.legend.labels.boxWidth = 16;
  defaults.global.legend.labels.padding = 20;
  defaults.global.hover.animationDuration = 400;
  defaults.global.tooltips.enabled = false;
  defaults.global.tooltips.intersect = true;
  defaults.global.tooltips.mode = 'nearest';
  defaults.global.tooltips.position = 'nearest';
  defaults.global.tooltips.xPadding = 10;
  defaults.global.tooltips.yPadding = 10;
  defaults.global.layout = {
    padding: {
      top: 10,
      right: 15,
      bottom: 10,
      left: 15,
    },
  };

  // http://www.chartjs.org/docs/latest/configuration/tooltip.html#external-custom-tooltips
  defaults.global.hover.onHover = function poinOnHover(e) {
    const point = this.getElementAtEvent(e);
    if (point.length) e.target.style.cursor = 'pointer';
    else e.target.style.cursor = 'default';
  };

  defaults.global.legend.onHover = function legendOnHover(e) {
    e.target.style.cursor = 'pointer';
  };

  defaults.global.tooltips.custom = function customTooltip(tooltipModel) {
    const { _chart: chart } = this;
    const { canvas } = chart;
    const canvasContainer = canvas.parentElement;
    let tooltipEl = canvasContainer.querySelector('.chartjs-tooltip');

    // Create element on first render
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.className = 'chartjs-tooltip';
      tooltipEl.innerHTML = '<div style="max-width:75vw"><table></table></div>';
      canvasContainer.appendChild(tooltipEl);
    }

    // Hide if no tooltip
    if (tooltipModel.opacity === 0) {
      tooltipEl.style.visibility = 'hidden';
      tooltipEl.style.opacity = 0;
      return;
    }

    // Set caret Position
    tooltipEl.classList.remove('above', 'below', 'no-transform');
    if (tooltipModel.yAlign) {
      tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
      tooltipEl.classList.add('no-transform');
    }

    function getBody(bodyItem) {
      return bodyItem.lines;
    }

    // Set Text
    if (tooltipModel.body) {
      const titleLines = tooltipModel.title || [];
      const beforeBodyLines = tooltipModel.beforeBody || [];
      const bodyLines = tooltipModel.body.map(getBody);
      const afterBodyLines = tooltipModel.afterBody || [];
      const footerLines = tooltipModel.footer || [];

      let innerHtml = '<thead>';

      titleLines.forEach(title => {
        innerHtml += `<tr><th ${
          chart.options.doNotTranslateTooltipTitle ? 'data-notranslate' : ''
        }>${title}</th></tr>`;
      });
      innerHtml += '</thead><tbody>';

      beforeBodyLines.forEach(bodyLine => {
        innerHtml += `<tr><td><span class="chartjs-tooltip-bodyline">${bodyLine}</span></td></tr>`;
      });

      bodyLines.forEach((body, i) => {
        const colors = tooltipModel.labelColors[i];
        let style = `background:${colors.backgroundColor}`;
        style += `; border-color:${colors.borderColor}`;
        style += '; border-width: 2px';

        const { datasetIndex } = tooltipModel.dataPoints[0];
        const isDashed = isEqual(
          chart.data.datasets[datasetIndex].borderDash,
          [5, 5]
        );

        const borderStyle = isDashed ? 'dashed' : 'solid';
        style += `; border-style: ${borderStyle}`;

        const span = `<span class="chartjs-tooltip-key" style="${style}"></span>`;
        innerHtml += `<tr><td>${span}<span class="chartjs-tooltip-value" ${
          chart.options.doNotTranslateTooltipValue ? 'data-notranslate' : ''
        }>${body}</span></td></tr>`;
      });

      afterBodyLines.forEach(bodyLine => {
        innerHtml += `<tr><td><span class="chartjs-tooltip-bodyline">${bodyLine}</span></td></tr>`;
      });

      innerHtml += '</tbody><tfoot>';
      footerLines.forEach(footer => {
        innerHtml += `<tr><th ${
          chart.options.doNotTranslateTooltipTitle ? 'data-notranslate' : ''
        }>${footer}</th></tr>`;
      });
      innerHtml += '</tfoot>';

      const tableRoot = tooltipEl.querySelector('table');
      tableRoot.innerHTML = innerHtml;
    }

    // Display, position, and set styles for font
    tooltipEl.style.visibility = 'visible';
    tooltipEl.style.opacity = 1;

    // where the tooltip should go
    const sumOfCanvasTooltipModelLeft = canvas.offsetLeft + tooltipModel.caretX;
    const tooltipElemOffsetWidth = tooltipEl.offsetWidth * 0.5;
    let tooltipLeft = sumOfCanvasTooltipModelLeft - tooltipElemOffsetWidth;

    const sumOfCanvasTooltipModelTop = canvas.offsetTop + tooltipModel.caretY;
    const tooltipElemOffsetHeight = tooltipEl.offsetHeight - 15;

    const tooltipTop = sumOfCanvasTooltipModelTop - tooltipElemOffsetHeight;

    const tooltipLeftAbsolute =
      canvasContainer.getBoundingClientRect().left + tooltipLeft;
    if (tooltipLeftAbsolute < 10) {
      tooltipLeft = 10 - canvasContainer.getBoundingClientRect().left;
    }

    const tooltipRightAbsolute =
      canvasContainer.getBoundingClientRect().left +
      tooltipLeft +
      tooltipEl.offsetWidth;

    if (tooltipRightAbsolute > document.documentElement.clientWidth) {
      const offset =
        canvasContainer.getBoundingClientRect().left +
        tooltipEl.offsetWidth +
        10;
      tooltipLeft = document.documentElement.clientWidth - offset;
    }

    tooltipEl.style.left = `${tooltipLeft}px`;
    tooltipEl.style.top = `${tooltipTop}px`;
    tooltipEl.style.padding = `${tooltipModel.yPadding}px ${tooltipModel.xPadding}px`;
  };
}
